import React from "react";
import { Box, Button, Link, MenuItem } from "@mui/material";
import { useNavigate } from "react-router-dom";
import {
  IDataTableProps,
  RenderType,
} from "../../../../../../components/Table/interfaces/IDataTableProps";
import { DataTable } from "../../../../../../components/Table/DataTable";
import { useDispatchWrapper } from "../../../../../../hooks";
import { IStoreState } from "../../../../../../redux/initialStoreState";
import { useSelector } from "react-redux";
import { LoadState } from "../../../../../../constants/enums";
import { StandardTableActions } from "../../../../../../components/StandardTableActions/StandardTableActions";
import { IBusinessPropertyInsurance } from "../../../../../../redux/businessPolicy/businessPropertyInsurance/businessPropertyInsurance.types";
import {
  downloadBusinessPropertyInsuranceListPDFAsync,
  fetchBusinessPropertyInsuranceListAsync,
  previewBusinessPropertyInsuranceListPDFAsync,
} from "../../../../../../redux/businessPolicy/businessPropertyInsurance/businessPropertyInsuranceActions";
import { CustomSelect } from "../../../../../../components/formsComponents";
import { renderDropdownValue } from "../../../../../../helpers";
import { CustomChip } from "../../../../../../components/CustomChip";
import { NotificationsActive, ThumbUpAlt } from "@mui/icons-material";

export const PropertyInsuranceCertificateList: React.FC<{
  customerId: number;
  customerPolicyId: number;
}> = (props) => {
  const { customerId, customerPolicyId } = props;
  const navigate = useNavigate();

  const {
    businessPropertyInsuranceList: data,
    businessPropertyInsuranceListLoading: loading,
  } = useSelector((storeState: IStoreState) => storeState.business.insurance);
  const [downloading, setDownloading] = React.useState(false);
  const dispatch = useDispatchWrapper();

  const [selectedInsurance, setselectedInsurance] = React.useState<{
    isAllSelected: boolean;
    insurance: IBusinessPropertyInsurance[];
  }>({
    isAllSelected: false,
    insurance: [],
  });

  const menuItems =
    selectedInsurance.insurance.length > 0 || selectedInsurance.isAllSelected
      ? [
          {
            key: "action",
            onRender: () => (
              <CustomSelect
                sx={{ minWidth: "120px" }}
                value={""}
                displayEmpty
                renderValue={renderDropdownValue(`Choose bulk Action`)}
                //onChange={(e) => setBranchOffice(e.target.value as string)}
              >
                <MenuItem value="1">Email</MenuItem>
              </CustomSelect>
            ),
          },
        ]
      : [];

  const PropertyTableProps: IDataTableProps = {
    isPagination: false,
    isDataLoading: loading === LoadState.InProgress,
    selectionMode: "multiple",
    uniqueRowKeyName: "customer_policy_id",
    tableCommandBarProps: {
      leftItems: {
        customPlugins: [...menuItems],
      },
      rightItems: {
        customPlugins: [
          {
            key: "status",
            onRender: () => (
              <Button
                variant="contained"
                onClick={() => {
                  navigate(
                    `/business-policy/${customerId}/policy-forms-tab/${customerPolicyId}/property-insurance`
                  );
                }}
              >
                Add Property Insurance
              </Button>
            ),
          },
        ],
      },
    },
    columns: [
      {
        key: "customer_policy_id",
        headerName: "ID",
        fieldName: "customer_policy_id",
        renderType: RenderType.CUSTOM_RENDER,
        exportCellWidth: 15,
        onRowCellRender: (value, row: IBusinessPropertyInsurance) => {
          return (
            <Link
              sx={{ cursor: "pointer" }}
              onClick={() =>
                navigate(
                  `/business-policy/${customerId}/policy-forms-tab/${customerPolicyId}/property-insurance/${row.customer_policy_id}`
                )
              }
            >
              {row.incremental_number}
            </Link>
          );
        },
      },

      {
        key: "status",
        headerName: "Status",
        fieldName: "status",
        renderType: RenderType.CUSTOM_RENDER,
        onRowCellRender: (value, row: IBusinessPropertyInsurance) => {
          if (row.status === "ACTIVE") {
            return (
              <CustomChip
                color="success"
                icon={<ThumbUpAlt />}
                content={"Active"}
                size="small"
              />
            );
          } else {
            return (
              <CustomChip
                color="error"
                icon={<NotificationsActive />}
                content={"Cancel"}
                size="small"
              />
            );
          }
        },
      },

      {
        key: "cgl",
        headerName: "CGL",
        fieldName: "cgl",
        renderType: RenderType.TEXT_DARK,
        exportCellWidth: 30,
      },

      {
        key: "certificate_holder_name",
        headerName: "Certificate Holder Name",
        fieldName: "certificate_holder_name",
        renderType: RenderType.TEXT,
        exportCellWidth: 30,
      },
      {
        key: "certificate_holder_address",
        headerName: "Certificate Holder Address",
        fieldName: "certificate_holder_address",
        renderType: RenderType.TEXT,
        exportCellWidth: 20,
      },
      {
        key: "additional_name_insured_name",
        headerName: "Insured Name",
        fieldName: "additional_name_insured_name",
        renderType: RenderType.TEXT_DARK,
        exportCellWidth: 20,
      },

      {
        key: "view",
        headerName: "Actions",
        fieldName: "",
        renderType: RenderType.CUSTOM_RENDER,
        onRowCellRender: (value, row: IBusinessPropertyInsurance) => {
          return (
            <StandardTableActions
              downloadLoading={downloading}
              onDownloadPreview={() => {
                dispatch(
                  previewBusinessPropertyInsuranceListPDFAsync(
                    customerPolicyId,
                    row.incremental_number as number,
                    () => {}
                  )
                );
              }}
              onDownLoadClick={() => {
                if (row.incremental_number) {
                  setDownloading(true);
                  dispatch(
                    downloadBusinessPropertyInsuranceListPDFAsync(
                      customerPolicyId,
                      row.incremental_number,
                      () => {
                        setDownloading(false);
                      }
                    )
                  );
                }
              }}
              onEditClick={() => {
                navigate(
                  `/business-policy/${customerId}/policy-forms-tab/${customerPolicyId}/property-insurance/${row.incremental_number}`
                );
              }}
              onDuplicateClick={() => {
                navigate(
                  `/business-policy/${customerId}/policy-forms-tab/${customerPolicyId}/property-insurance-duplicate/${row.incremental_number}`
                );
              }}
            />
          );
        },
      },
    ],
    items: data,
    onSelection: (
      isAllSelected: boolean,
      rows: IBusinessPropertyInsurance[]
    ) => {
      setselectedInsurance({ isAllSelected: isAllSelected, insurance: rows });
    },
  };

  React.useEffect(() => {
    dispatch(fetchBusinessPropertyInsuranceListAsync(customerPolicyId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerPolicyId]);

  return (
    <>
      <Box sx={{ mt: 2 }}>
        <DataTable {...PropertyTableProps} />
      </Box>
    </>
  );
};
